(() => {
  const CONTROLLER_FLAG = 'data-hormonize-sticky-atc-js-ready';
  const ROOT_SELECTOR = '[data-hormonize-sticky-atc]';
  const PRICE_SELECTOR = '[data-hormonize-sticky-atc-price]';
  const COMPARE_SELECTOR = '[data-hormonize-sticky-atc-compare]';
  const MEDIA_SELECTOR = '[data-hormonize-sticky-atc-media]';
  const TRIGGER_SELECTOR = '[data-hormonize-sticky-atc-trigger]';
  const QTY_INPUT_SELECTOR = '[data-hormonize-sticky-atc-qty-input]';
  const QTY_MINUS_SELECTOR = '[data-hormonize-sticky-atc-qty-minus]';
  const QTY_PLUS_SELECTOR = '[data-hormonize-sticky-atc-qty-plus]';
  const VISIBLE_CLASS = 'is-visible';

  /* Observe the full hero/section — not sticky buy-box children. */
  const OBSERVER_TARGETS = [
    '[data-hormonize-product-hero]',
    '.hormonize-product-hero',
    '[id^="HormonizeProductHero-"]',
    '[data-hormonize-main-product]',
    '.hormonize-main-product',
    '[id^="MainProduct-"]',
    '.product',
    '[id^="ProductInfo-"]',
    '.product__info-container',
  ];

  const instances = new WeakMap();

  function formatMoneyFromCents(cents) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) {
      return '';
    }

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const format =
        (window.theme && theme.moneyFormat) ||
        (window.Shopify && Shopify.money_format) ||
        '{{amount}}';
      return Shopify.formatMoney(cents, format);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function findObserverTarget(formSectionId) {
    if (formSectionId) {
      const scopedHero = document.getElementById(`HormonizeProductHero-${formSectionId}`);
      if (scopedHero) {
        return scopedHero.closest('.shopify-section') || scopedHero;
      }

      const scopedMain = document.getElementById(`MainProduct-${formSectionId}`);
      if (scopedMain) {
        return scopedMain.closest('.shopify-section') || scopedMain;
      }

      const scopedInfo = document.getElementById(`ProductInfo-${formSectionId}`);
      if (scopedInfo) {
        return scopedInfo.closest('.shopify-section') || scopedInfo;
      }
    }

    for (let i = 0; i < OBSERVER_TARGETS.length; i += 1) {
      const match = document.querySelector(OBSERVER_TARGETS[i]);
      if (match) {
        return match.closest('.shopify-section') || match;
      }
    }

    return null;
  }

  function isPastTarget(target) {
    if (!(target instanceof Element)) {
      return false;
    }

    return target.getBoundingClientRect().bottom <= 0;
  }

  function findProductForm(formSectionId) {
    if (formSectionId) {
      const scoped =
        document.getElementById(`product-form-${formSectionId}`) ||
        document.querySelector(`#product-form-${formSectionId}`) ||
        document.querySelector(`product-form[data-section-id="${formSectionId}"] form[action*="/cart/add"]`);
      if (scoped) {
        return scoped.tagName === 'FORM' ? scoped : scoped.querySelector('form[action*="/cart/add"]') || scoped;
      }
    }

    return (
      document.querySelector('product-form form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"][id^="product-form-"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function findQuantityInput(formSectionId) {
    const form = findProductForm(formSectionId);
    if (form) {
      const inForm = form.querySelector('input[name="quantity"]');
      if (inForm) {
        return inForm;
      }
    }

    if (formSectionId) {
      const scoped = document.querySelector(
        `#ProductInfo-${formSectionId} input[name="quantity"], #HormonizeProductHero-${formSectionId} input[name="quantity"]`
      );
      if (scoped) {
        return scoped;
      }
    }

    return document.querySelector(
      '[data-hormonize-product-hero] input[name="quantity"], .hormonize-product-hero input[name="quantity"], input[name="quantity"][form^="product-form"]'
    );
  }

  function findSubmitButton(formSectionId) {
    if (formSectionId) {
      const scoped = document.getElementById(`ProductSubmitButton-${formSectionId}`);
      if (scoped) {
        return scoped;
      }
    }

    const anySubmit = document.querySelector('[id^="ProductSubmitButton-"]');
    if (anySubmit) {
      return anySubmit;
    }

    return document.querySelector(
      '[data-hormonize-product-hero] .product-form__submit, .hormonize-product-hero .product-form__submit, .product-form__submit, form[action*="/cart/add"] [type="submit"]'
    );
  }

  function findScrollTarget(formSectionId) {
    if (formSectionId) {
      const form = document.getElementById(`product-form-${formSectionId}`);
      if (form) {
        return form;
      }
    }

    return (
      document.querySelector('[data-hormonize-product-hero]') ||
      document.querySelector('.hormonize-product-hero') ||
      document.querySelector('[data-hormonize-main-product]') ||
      document.querySelector('.product-form__buttons') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function extractPriceParts(html, sectionId) {
    const empty = { price: '', compare: '' };

    if (!(html instanceof Document || html instanceof Element)) {
      return empty;
    }

    let priceRoot = null;

    if (sectionId) {
      priceRoot = html.getElementById(`price-${sectionId}`);
    }

    if (!priceRoot) {
      priceRoot = html.querySelector('[id^="price-"]');
    }

    if (!priceRoot) {
      return empty;
    }

    const saleItem =
      priceRoot.querySelector('.price-item--sale:not(.price-item--last)') ||
      priceRoot.querySelector('.price__sale .price-item--sale');
    const regularItem =
      priceRoot.querySelector('.price-item--regular') ||
      priceRoot.querySelector('.price-item--last') ||
      priceRoot.querySelector('.price-item');
    const compareItem =
      priceRoot.querySelector('.price__sale s .price-item--regular') ||
      priceRoot.querySelector('.price__sale s') ||
      priceRoot.querySelector('s .price-item--regular') ||
      priceRoot.querySelector('s');

    const priceText = (saleItem?.textContent || regularItem?.textContent || priceRoot.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    const compareText = (compareItem?.textContent || '').replace(/\s+/g, ' ').trim();

    return {
      price: priceText,
      compare: compareText && compareText !== priceText ? compareText : '',
    };
  }

  class HormonizeStickyAtc {
    constructor(root) {
      this.root = root;
      this.priceEl = root.querySelector(PRICE_SELECTOR);
      this.compareEl = root.querySelector(COMPARE_SELECTOR);
      this.mediaEl = root.querySelector(MEDIA_SELECTOR);
      this.trigger = root.querySelector(TRIGGER_SELECTOR);
      this.qtyInput = root.querySelector(QTY_INPUT_SELECTOR);
      this.qtyMinus = root.querySelector(QTY_MINUS_SELECTOR);
      this.qtyPlus = root.querySelector(QTY_PLUS_SELECTOR);
      this.formSectionId = (root.getAttribute('data-hormonize-sticky-atc-form-section') || '').trim();
      this.buttonLabel = root.getAttribute('data-hormonize-sticky-atc-button-label') || 'Add to cart';
      this.soldOutLabel = root.getAttribute('data-hormonize-sticky-atc-sold-out-label') || 'Sold out';
      this.observer = null;
      this.observerTarget = null;
      this.unsubscribeVariant = null;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onQtyInputChange = this.onQtyInputChange.bind(this);
      this.onQtyMinus = this.onQtyMinus.bind(this);
      this.onQtyPlus = this.onQtyPlus.bind(this);
      this.onScrollOrResize = this.onScrollOrResize.bind(this);

      this.mountToBody();
      this.bind();
      this.observe();
      this.subscribeVariant();
      this.syncQuantityFromForm();
      this.syncAvailabilityFromSubmit();
      this.updateVisibility();
    }

    mountToBody() {
      if (this.root.parentElement === document.body) {
        return;
      }

      document.body.appendChild(this.root);
    }

    bind() {
      this.trigger?.addEventListener('click', this.onTriggerClick);
      this.qtyInput?.addEventListener('change', this.onQtyInputChange);
      this.qtyInput?.addEventListener('input', this.onQtyInputChange);
      this.qtyMinus?.addEventListener('click', this.onQtyMinus);
      this.qtyPlus?.addEventListener('click', this.onQtyPlus);
      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize);
    }

    setVisible(isVisible) {
      const shouldShow = Boolean(isVisible);
      this.root.classList.toggle(VISIBLE_CLASS, shouldShow);
      this.root.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

      if (shouldShow) {
        this.root.removeAttribute('hidden');
      } else {
        this.root.setAttribute('hidden', '');
      }
    }

    updateVisibility() {
      if (!this.observerTarget) {
        this.setVisible(true);
        return;
      }

      this.setVisible(isPastTarget(this.observerTarget));
    }

    onScrollOrResize() {
      this.updateVisibility();
    }

    observe() {
      this.observerTarget = findObserverTarget(this.formSectionId);

      if (!this.observerTarget) {
        this.setVisible(true);
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        this.updateVisibility();
        return;
      }

      this.observer = new IntersectionObserver(
        () => {
          this.updateVisibility();
        },
        {
          root: null,
          threshold: [0, 0.01, 1],
        }
      );

      this.observer.observe(this.observerTarget);
    }

    subscribeVariant() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        return;
      }

      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    getQtyBounds() {
      const min = Math.max(1, Number(this.qtyInput?.min) || 1);
      const step = Math.max(1, Number(this.qtyInput?.step) || 1);
      const maxRaw = this.qtyInput?.max;
      const max = maxRaw === '' || maxRaw == null ? null : Number(maxRaw);

      return { min, step, max: Number.isFinite(max) ? max : null };
    }

    clampQuantity(value) {
      const { min, step, max } = this.getQtyBounds();
      let next = Number(value);

      if (!Number.isFinite(next)) {
        next = min;
      }

      next = Math.max(min, next);

      if (max != null) {
        next = Math.min(max, next);
      }

      const offset = next - min;
      next = min + Math.round(offset / step) * step;

      if (max != null) {
        next = Math.min(max, next);
      }

      return Math.max(min, next);
    }

    setQuantity(value, { syncForm = true } = {}) {
      if (!this.qtyInput) {
        return;
      }

      const next = this.clampQuantity(value);
      this.qtyInput.value = String(next);

      if (syncForm) {
        this.syncQuantityToForm(next);
      }
    }

    syncQuantityToForm(quantity) {
      const formQty = findQuantityInput(this.formSectionId);
      if (!formQty) {
        return;
      }

      formQty.value = String(quantity);
      formQty.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncQuantityFromForm() {
      const formQty = findQuantityInput(this.formSectionId);
      if (!formQty || !this.qtyInput) {
        return;
      }

      this.setQuantity(formQty.value || this.qtyInput.min || 1, { syncForm: false });
    }

    onQtyInputChange() {
      this.setQuantity(this.qtyInput?.value);
    }

    onQtyMinus(event) {
      event.preventDefault();
      const { step } = this.getQtyBounds();
      const current = Number(this.qtyInput?.value) || this.getQtyBounds().min;
      this.setQuantity(current - step);
    }

    onQtyPlus(event) {
      event.preventDefault();
      const { step } = this.getQtyBounds();
      const current = Number(this.qtyInput?.value) || this.getQtyBounds().min;
      this.setQuantity(current + step);
    }

    setComparePrice(compareText) {
      if (!this.compareEl) {
        return;
      }

      if (compareText) {
        this.compareEl.textContent = compareText;
        this.compareEl.classList.remove('is-hidden');
        this.compareEl.removeAttribute('hidden');
        return;
      }

      this.compareEl.textContent = '';
      this.compareEl.classList.add('is-hidden');
      this.compareEl.setAttribute('hidden', '');
    }

    setPrice(priceText, compareText) {
      if (priceText && this.priceEl) {
        this.priceEl.textContent = priceText;
      }

      this.setComparePrice(compareText || '');
    }

    setMedia(variant) {
      if (!this.mediaEl) {
        return;
      }

      const imageEl = this.mediaEl.querySelector('img');
      const media = variant?.featured_media;
      const preview = media?.preview_image || media;
      const src = preview?.src;

      if (!src || !imageEl) {
        return;
      }

      const width = 200;
      const url = src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
      imageEl.src = url;
      if (imageEl.srcset) {
        imageEl.srcset = `${url} ${width}w`;
      }
      if (preview.alt) {
        imageEl.alt = preview.alt;
      }
      this.mediaEl.classList.remove('is-empty');
    }

    setAvailability(isAvailable) {
      if (!this.trigger) {
        return;
      }

      if (isAvailable) {
        this.trigger.removeAttribute('disabled');
        this.trigger.removeAttribute('aria-disabled');
        this.trigger.textContent = this.buttonLabel;
        return;
      }

      this.trigger.setAttribute('disabled', '');
      this.trigger.setAttribute('aria-disabled', 'true');
      this.trigger.textContent = this.soldOutLabel;
    }

    syncAvailabilityFromSubmit() {
      const submitButton = findSubmitButton(this.formSectionId);
      if (!submitButton) {
        return;
      }

      const unavailable =
        submitButton.hasAttribute('disabled') || submitButton.getAttribute('aria-disabled') === 'true';
      this.setAvailability(!unavailable);
    }

    onVariantChange(event) {
      const data = event?.data;
      if (!data) {
        return;
      }

      if (this.formSectionId && data.sectionId && data.sectionId !== this.formSectionId) {
        return;
      }

      const priceParts = extractPriceParts(data.html, data.sectionId);
      let priceText = priceParts.price;
      let compareText = priceParts.compare;

      if ((!priceText || !compareText) && data.variant) {
        if (!priceText && typeof data.variant.price === 'number') {
          priceText = formatMoneyFromCents(data.variant.price);
        }

        if (
          !compareText &&
          typeof data.variant.compare_at_price === 'number' &&
          data.variant.compare_at_price > data.variant.price
        ) {
          compareText = formatMoneyFromCents(data.variant.compare_at_price);
        }
      }

      if (priceText) {
        this.setPrice(priceText, compareText);
      }

      this.setMedia(data.variant);
      this.setAvailability(Boolean(data.variant?.available));

      if (data.variant?.id && this.trigger) {
        this.trigger.setAttribute('data-variant-id', String(data.variant.id));
        this.root.setAttribute('data-hormonize-sticky-atc-variant-id', String(data.variant.id));
      }

      if (this.qtyInput && data.variant?.quantity_rule) {
        const rule = data.variant.quantity_rule;
        if (rule.min != null) {
          this.qtyInput.min = String(rule.min);
        }
        if (rule.max != null) {
          this.qtyInput.max = String(rule.max);
        } else {
          this.qtyInput.removeAttribute('max');
        }
        if (rule.increment != null) {
          this.qtyInput.step = String(rule.increment);
        }
        this.setQuantity(this.qtyInput.value);
      }
    }

    onTriggerClick(event) {
      event.preventDefault();

      if (this.qtyInput) {
        this.setQuantity(this.qtyInput.value);
      }

      const submitButton = findSubmitButton(this.formSectionId);

      if (submitButton && !submitButton.hasAttribute('disabled') && submitButton.getAttribute('aria-disabled') !== 'true') {
        submitButton.click();
        return;
      }

      const scrollTarget = findScrollTarget(this.formSectionId);
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (typeof scrollTarget.focus === 'function') {
          try {
            scrollTarget.focus({ preventScroll: true });
          } catch (error) {
            scrollTarget.focus();
          }
        }
      }
    }

    destroy() {
      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.qtyInput?.removeEventListener('change', this.onQtyInputChange);
      this.qtyInput?.removeEventListener('input', this.onQtyInputChange);
      this.qtyMinus?.removeEventListener('click', this.onQtyMinus);
      this.qtyPlus?.removeEventListener('click', this.onQtyPlus);
      window.removeEventListener('scroll', this.onScrollOrResize);
      window.removeEventListener('resize', this.onScrollOrResize);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (typeof this.unsubscribeVariant === 'function') {
        this.unsubscribeVariant();
        this.unsubscribeVariant = null;
      }

      this.setVisible(false);
      this.root.remove();
    }
  }

  function initSticky(root) {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) {
      return;
    }

    if (instances.has(root)) {
      return;
    }

    instances.set(root, new HormonizeStickyAtc(root));
  }

  function initStickies(searchRoot = document) {
    const root = searchRoot instanceof Element || searchRoot instanceof Document ? searchRoot : document;

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      initSticky(root);
    }

    root.querySelectorAll(ROOT_SELECTOR).forEach(initSticky);
  }

  function destroyStickies(searchRoot) {
    const root = searchRoot instanceof Element || searchRoot instanceof Document ? searchRoot : document;
    const bars = [];

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      bars.push(root);
    }

    root.querySelectorAll(ROOT_SELECTOR).forEach((bar) => {
      bars.push(bar);
    });

    bars.forEach((bar) => {
      const instance = instances.get(bar);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(bar);
    });
  }

  function onSectionLoad(event) {
    initStickies(event.target);
  }

  function onSectionUnload(event) {
    const section = event.target;

    if (section instanceof Element && section.classList.contains('section-hormonize-sticky-atc')) {
      destroyStickies(document);
      return;
    }

    destroyStickies(section);
  }

  function bindLifecycleEvents() {
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:section:reorder', () => {
      initStickies(document);
    });
  }

  if (!document.documentElement.hasAttribute(CONTROLLER_FLAG)) {
    document.documentElement.setAttribute(CONTROLLER_FLAG, '');
    bindLifecycleEvents();
  }

  initStickies(document);
})();
